import { CalcSeedMapCmd } from "../../calcSeedMap/calcSeedMapCmd"
import { DrawSeedMapCmd } from "../../drawSeedMap/drawSeedMapCmd"
import { SeedsMap } from "../../map/seedsMap"
import { defineDirective, FieldViewModel, IComponentContext, IDataLayer, IDatalayerContext, IDatalayerMapDependencies, ILayerViewModel, IOmniaCommand, ISeedRateLayer, IVariety, SharedLayersService } from "../../exports"
import { IPlanCropViewModel, ISeedRatePlan, ISeedsService } from "../../types"
import { getSeedQtyForMap } from "../../utils"
import { LayerViewModel } from "@omnia/shared/layers/layerViewModel"
import { getTotalQtyLabel } from "../../units"
import { showLayers } from "@omnia/shared/layers/utils"

export function createPlanCroppingList(): ng.IDirective {

    return defineDirective(PlanCroppingCtrl, "app/seeds/plan/cropping/cropping.html", {
        map: "=",
        selectedPlan: "=",
        selectedCropping: "=",
        componentContext: "=",
        readOnly: "="
    });

};

export class PlanCroppingCtrl {

    croppingCommands: IOmniaCommand[][];
    private _calcPlanCmd: CalcSeedMapCmd;
    private _drawCmd: DrawSeedMapCmd;

    public map: SeedsMap;

    private _layerContext: IDatalayerContext;

    public selectedPlan: ISeedRatePlan;
    private _previousPlan: ISeedRatePlan;

    private componentContext: IComponentContext;

    public cropping: IPlanCropViewModel[];

    public selectedCropping: FieldViewModel;

    public readOnly: boolean

    public variety: IVariety

    static $inject = [
        "$scope",
        "OmniaSeedsService",
        "OmniaDatalayersDependencies",
        "OmniaDatalayersService"
    ]

    constructor(
        private readonly $scope: ng.IScope,
        private readonly svc: ISeedsService,
        private readonly mapDependencies: IDatalayerMapDependencies,
        private readonly layersSvc: SharedLayersService) {
    }

    $onInit() {

        const { $scope, mapDependencies } = this

        this._layerContext = mapDependencies.createContext(() => this.map ? this.map.getDataLayers() : [])

        $scope.$watchGroup(["vm.map", "vm.selectedPlan", "vm.plans", "vm.componentContext"], () => this.setup());

        $scope.$watch("vm.selectedCropping", (selected: FieldViewModel, previous: FieldViewModel) => this.onSelectionChanged(selected, previous));

    }

    $onDestroy() {
        this.dispose()
    }

    get yearId(): number {
        return this.svc.yearId;
    }

    private dispose() {

        if (this.cropping) {
            this.cropping.forEach(c => {
                if (c.layer) c.layer.dispose();
            });
        }

    }

    private setup() {

        var map = this.map;
        var plan = this.selectedPlan;

        if (!(map && plan)) {

            this.cropping = [];

        }
        else {

            this.cropping = this.svc.getPlanCropping(plan, false);

            if (this.selectedCropping) {
                this.onSelectionChanged(this.selectedCropping, null);
            }

        }

        this.variety = plan
            ? this.svc.getVariety(plan.VarietyId)
            : undefined

        this.setupCommands();

        this.setupLayers();

        this.map.viewExtentForDataItems(this.cropping.map(c => c.croppingVm));

    }


    private setupLayers() {

        this.cropping.forEach(c => {

            const map = c.planCropping

            if (map && map.Layer) {

                const layerVm = new LayerViewModel(this._layerContext, this.map, map.Layer, c.fieldVm);

                c.layer = layerVm;
                c.layer.visible = this.svc.getLayerVisibility(map.LayerId);

            }

        });

        this.restoreState();

        if (this._previousPlan != this.selectedPlan) {

            this._previousPlan = this.selectedPlan;

            const layers = this.cropping.map(c => c.layer).filter(l => !!l)

            showLayers(this._layerContext, layers)

        }

    }

    private setupCommands() {

        this._calcPlanCmd = new CalcSeedMapCmd(this.componentContext, "Calculate a new seed plan", "calc");
        this._calcPlanCmd.execute = (cropping: IPlanCropViewModel) => this._calcPlanCmd.calcSeedRatesMap(this.selectedPlan, [cropping.croppingVm]);

        this._drawCmd = new DrawSeedMapCmd(
            this.componentContext,
            this.svc,
            this.layersSvc,
            "Draw a new seed rate map", "draw"
        )

        this.croppingCommands = [
            [this._calcPlanCmd],
            [this._drawCmd],
        ];

    }

    private restoreState(): boolean {

        var cropping = (this.svc.state.expandedCropping ? this.svc.state.expandedCropping.slice() : null);

        if (cropping) {

            cropping.slice().forEach(id => {
                var cropping = this.cropping.find(c => c.croppingId == id);
                if (cropping) cropping.expanded = true;
            });

            return true;

        }

    }

    isPlanFlatRate() {
        return this.selectedPlan?.UseFlatRate
    }

    private onSelectionChanged(selected: FieldViewModel, previous: FieldViewModel) {

        if (selected && this.cropping) {

            var vm = this.cropping.find(c => c.croppingId == selected.croppingId);

            if (vm) {

                this.toggleCropping(vm, true);

                if (previous) {
                    vm = this.cropping.find(c => c.croppingId == previous.croppingId);
                    if (vm) this.toggleCropping(vm, false);
                }

            }

        }

    }

    editLayer(cropping: IPlanCropViewModel, lvm: ILayerViewModel) {

        var layer = lvm.datalayer;

        if ((!layer) || layer.Zones) {

            this.editLoadedLayer(cropping, layer);

        }
        else {

            this.svc.loadLayerZones([layer]).then(r => {
                this.editLoadedLayer(cropping, layer);
            });

        }
    }

    private editLoadedLayer(cropping: IPlanCropViewModel, layer: IDataLayer) {

        var layerId = (layer ? layer.DataLayerId : 0);

        if (layerId) {
            this.svc.setLayerVisibility(layerId, true);
        }

        this._drawCmd.drawSeedsMap(this.selectedPlan, cropping.croppingVm, layer);

    }


    toggleCropping(cropping: IPlanCropViewModel, expand: boolean) {

        cropping.expanded = !cropping.expanded || expand;

        if ((!expand) && cropping.expanded) {
            this.locateCropping(cropping);
            if (cropping.layer) {
                cropping.layer.visible = true;
            }
        }

        this.svc.state.expandedCropping = this.cropping.filter(c => c.expanded).map(c => c.croppingId);

    }

    onMapVisibilityChanged(cropping: IPlanCropViewModel, layer: ILayerViewModel) {

        if (layer.visible) {
            this.locateCropping(cropping);
        }

        this.svc.setLayerVisibility(layer.datalayerId, layer.visible);
    }

    private locateCropping(cropping: IPlanCropViewModel) {
        var feature = this.map.findFeatureWithData(cropping.croppingVm);
        this.map.viewExtent(feature.getGeometry());
    }

    public getTotalQtyLabel(cropping: IPlanCropViewModel) {

        const { selectedPlan: plan } = this

        if (cropping && cropping.layer) {

            const layerVm = cropping.layer;
            const layer = <ISeedRateLayer>layerVm.datalayer;

            const qty = getSeedQtyForMap(plan, layer.Seeds);

            return getTotalQtyLabel(qty, plan.Unit, true)
        }


    }

    public recalculate(cropping: IPlanCropViewModel, layer: ILayerViewModel) {

        // Note: the @layer argument is currently ignored, because we now
        // assume that there will only be one single layer for each cropping in a plan
        this._calcPlanCmd.calcSeedRatesMap(this.selectedPlan, [cropping.croppingVm]);

    }

}
